import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, Kart, KartIcerik, toast } from '@emanetx/ui';
import { eposta, type SmtpAyar } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

interface SmtpForm {
  host: string;
  port: string; // textbox
  secure: boolean;
  user: string;
  sifre: string;
  fromEmail: string;
  fromName: string;
}

const BOS_FORM: SmtpForm = {
  host: '',
  port: '587',
  secure: false,
  user: '',
  sifre: '',
  fromEmail: '',
  fromName: '',
};

export function SayfaSmtp() {
  const qc = useQueryClient();
  const [form, setForm] = useState<SmtpForm>(BOS_FORM);
  const [testAlici, setTestAlici] = useState('');

  const ayarSorgu = useQuery({
    queryKey: ['eposta', 'smtp'],
    queryFn: () => eposta.smtpGetir(istemci),
  });

  // Sorgu dönünce form'u doldur — sifre alani DAIMA bos
  useEffect(() => {
    if (ayarSorgu.data) {
      setForm({
        host: ayarSorgu.data.host,
        port: String(ayarSorgu.data.port || 587),
        secure: ayarSorgu.data.secure,
        user: ayarSorgu.data.user,
        sifre: '',
        fromEmail: ayarSorgu.data.fromEmail,
        fromName: ayarSorgu.data.fromName,
      });
    }
  }, [ayarSorgu.data]);

  const kaydet = useMutation({
    mutationFn: () => {
      const giris: Partial<SmtpAyar> = {
        host: form.host,
        port: Number(form.port),
        secure: form.secure,
        user: form.user,
        fromEmail: form.fromEmail,
        fromName: form.fromName,
      };
      if (form.sifre.length > 0) giris.sifre = form.sifre;
      return eposta.smtpKaydet(istemci, giris);
    },
    onSuccess: () => {
      toast.success('SMTP ayarları kaydedildi');
      setForm((f) => ({ ...f, sifre: '' }));
      void qc.invalidateQueries({ queryKey: ['eposta', 'smtp'] });
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  const test = useMutation({
    mutationFn: () => eposta.smtpTest(istemci, { alici: testAlici }),
    onSuccess: (sonuc) => {
      if (sonuc.basarili) toast.success('Test e-postası gönderildi');
      else toast.error(`Gönderilemedi: ${sonuc.hata ?? 'bilinmeyen'}`);
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  if (ayarSorgu.isLoading) return <Iskelet className="h-80" />;

  const formGecerli =
    form.host.length > 0 &&
    Number(form.port) > 0 &&
    form.user.length > 0 &&
    form.fromEmail.includes('@') &&
    form.fromName.length > 0 &&
    (ayarSorgu.data?.yapilandirildi || form.sifre.length > 0);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">SMTP Ayarları</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          E-posta gönderim sunucusu. Hoş geldin, doğrulama ve makbuz mailleri bu sunucudan gider.
        </p>
      </header>

      <Kart>
        <KartIcerik className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Alan
              etiket="Sunucu (host)"
              deger={form.host}
              setDeger={(v) => setForm({ ...form, host: v })}
              placeholder="smtp.gmail.com"
            />
            <Alan
              etiket="Port"
              deger={form.port}
              setDeger={(v) => setForm({ ...form, port: v.replace(/\D/g, '') })}
              placeholder="587"
            />
            <Alan
              etiket="Kullanıcı"
              deger={form.user}
              setDeger={(v) => setForm({ ...form, user: v })}
              placeholder="ornek@emanetx.com"
            />
            <Alan
              etiket={`Şifre ${ayarSorgu.data?.yapilandirildi ? '(değiştirmek istemiyorsan boş bırak)' : ''}`}
              deger={form.sifre}
              setDeger={(v) => setForm({ ...form, sifre: v })}
              placeholder={ayarSorgu.data?.yapilandirildi ? '••••••••' : 'Uygulama şifresi'}
              tip="password"
            />
            <Alan
              etiket="Gönderen E-posta"
              deger={form.fromEmail}
              setDeger={(v) => setForm({ ...form, fromEmail: v })}
              placeholder="info@emanetx.com"
            />
            <Alan
              etiket="Gönderen Adı"
              deger={form.fromName}
              setDeger={(v) => setForm({ ...form, fromName: v })}
              placeholder="EmanetX"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.secure}
              onChange={(e) => setForm({ ...form, secure: e.target.checked })}
              className="h-4 w-4"
            />
            <span>TLS/SSL kullan (port 465 için açık, 587 için kapalı)</span>
          </label>

          <div className="flex justify-end pt-2">
            <Buton onClick={() => kaydet.mutate()} disabled={!formGecerli || kaydet.isPending}>
              {kaydet.isPending ? 'Kaydediliyor...' : 'Kaydet'}
            </Buton>
          </div>
        </KartIcerik>
      </Kart>

      <Kart>
        <KartIcerik className="space-y-3">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-marka-600" />
            <h2 className="font-semibold">Test E-postası Gönder</h2>
          </div>
          <p className="text-fume-500 text-sm">
            Kaydedilen SMTP ayarlarıyla bir test maili gönderir.
          </p>
          <div className="flex gap-2">
            <Girdi
              type="email"
              value={testAlici}
              onChange={(e) => setTestAlici(e.target.value)}
              placeholder="alici@ornek.com"
              className="flex-1"
            />
            <Buton
              stil="cizgili"
              onClick={() => test.mutate()}
              disabled={!testAlici.includes('@') || test.isPending}
            >
              <Send size={14} /> {test.isPending ? 'Gönderiliyor...' : 'Gönder'}
            </Buton>
          </div>
        </KartIcerik>
      </Kart>
    </div>
  );
}

function Alan({
  etiket,
  deger,
  setDeger,
  placeholder,
  tip = 'text',
}: {
  etiket: string;
  deger: string;
  setDeger: (v: string) => void;
  placeholder?: string;
  tip?: 'text' | 'password';
}) {
  return (
    <div>
      <Etiket>{etiket}</Etiket>
      <Girdi
        type={tip}
        value={deger}
        onChange={(e) => setDeger(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function hataMesaji(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: string }).message);
  }
  return 'Bir hata oluştu';
}
